import produce from 'immer';
import uniq from 'lodash/uniq';
import type { Reducer } from 'react';
import removeUndefined from '../../../common/utils/removeUndefined';
import { MODE, TYPE_UPLOAD_FIELD } from '../../../constants';
import { ERRORS, type FormError } from '../FormErrors';
import { FORM_SUBMITTER_ACTIONS, type FormSubmitterAction } from './actions';

import type { FormSubmitterState } from './form-submitter-state';

const getValidationError = (
  notificationOverride: FormError | undefined,
  showFormGenericErrorMessage: boolean,
): FormError | undefined => {
  return showFormGenericErrorMessage
    ? ERRORS.FORM_GENERIC_ERROR_MESSAGE
    : notificationOverride || ERRORS.FORM_INCOMPLETE;
};

export type FormSubmitterReducer = Reducer<
  FormSubmitterState,
  FormSubmitterAction
>;

export const formSubmitterReducer: FormSubmitterReducer = (state, action) => {
  let newState = state;
  switch (action.type) {
    case FORM_SUBMITTER_ACTIONS.ADD_ITEMS_RESOLVED:
      {
        const { id, itemQueue, errorId, items } = action;
        newState = {
          ...state,
          hasChange: true,
          itemQueues: {
            ...state.itemQueues,
            [id]: itemQueue,
          },
          response: {
            ...state.response,
            [id]: {
              ...state.response[id],
              length: items.length,
            },
          },
          errors: removeUndefined({
            ...state.errors,
            [id]: errorId,
          }),
        };
      }
      break;
    case FORM_SUBMITTER_ACTIONS.REMOVE_ITEM_RESOLVED:
      {
        const { id, itemQueue, errorId, items } = action;
        const fieldResponse = state.response[id];
        newState = {
          ...state,
          itemQueues: {
            ...state.itemQueues,
            [id]: itemQueue,
          },
          response: {
            ...state.response,
            [id]: {
              ...fieldResponse,
              length: items?.length || 0,
            },
          },
          errors: removeUndefined({
            ...state.errors,
            [id]: errorId,
          }),
        };

        if (!items?.length && !fieldResponse?.description) {
          delete newState.response[id];
        }
      }
      break;
    case FORM_SUBMITTER_ACTIONS.SELECT_FIELD_ON_CHANGE:
    case FORM_SUBMITTER_ACTIONS.TAXONOMY_FIELD_ON_CHANGE:
    case FORM_SUBMITTER_ACTIONS.LIST_FIELD_ON_CHANGE:
      {
        const { id, newValues, errorId } = action;
        newState = {
          ...state,
          hasChange: true,
          response: {
            ...state.response,
            [id]: newValues,
          },
          errors: removeUndefined({
            ...state.errors,
            [id]: errorId,
          }),
        };
      }
      break;
    case FORM_SUBMITTER_ACTIONS.NUMBER_FIELD_ON_CHANGE:
    case FORM_SUBMITTER_ACTIONS.TEXT_FIELD_ON_CHANGE:
    case FORM_SUBMITTER_ACTIONS.DATE_FIELD_ON_CHANGE:
      {
        const { id, newValue, errorId } = action;
        newState = {
          ...state,
          hasChange: true,
          response: {
            ...state.response,
            [id]: newValue,
          },
          errors: removeUndefined({
            ...state.errors,
            [id]: errorId,
          }),
        };
      }
      break;
    case FORM_SUBMITTER_ACTIONS.UPLOAD_FIELD_FILE_DESCRIPTION_ON_CHANGE: {
      const { id, newValue, errorId } = action;

      newState = produce(state, (draftState: FormSubmitterState) => {
        if (draftState.components[id].type !== TYPE_UPLOAD_FIELD) {
          return;
        }

        draftState.hasChange = true;
        if (
          // typeof null === 'object'
          !draftState.response[id] ||
          typeof draftState.response[id] !== 'object'
        ) {
          draftState.response[id] = {};
        }

        draftState.response[id].description = newValue;
        draftState.errors = removeUndefined({
          ...state.errors,
          [id]: errorId,
        });
      });
      break;
    }
    case FORM_SUBMITTER_ACTIONS.TOGGLE_UPLOAD_MANAGER:
      newState = {
        ...state,
        isUploadManagerExpanded: !state.isUploadManagerExpanded,
      };
      break;
    case FORM_SUBMITTER_ACTIONS.TOGGLE_TOS_CHECKBOX:
      newState = {
        ...state,
        TosChecked: !state.TosChecked,
      };
      break;
    case FORM_SUBMITTER_ACTIONS.VALIDATION_FAILED:
      {
        const { errors: errorsWithNotification = {} } = action;
        const { notificationOverride, ...errors } = errorsWithNotification;

        const allErrorMessageIds = uniq(
          Object.values(errors).map(error => error.id),
        );
        const isIncompleteFormError = allErrorMessageIds.every(
          id =>
            id === ERRORS.REQUIRED_FIELD.id ||
            id === ERRORS.REQUIRED_FORM_UPLOAD_FIELD.id ||
            id === ERRORS.REQUIRED_FILE_DESCRIPTION.id,
        );
        const hasMultipleTypeErrors =
          allErrorMessageIds.length > 1 && !isIncompleteFormError;

        const showFormGenericErrorMessage =
          state.form.formType === MODE.FORM && hasMultipleTypeErrors;

        newState = {
          ...state,
          errors,
          isInvalid: true,
          errorNotificationMessage: getValidationError(
            notificationOverride,
            showFormGenericErrorMessage,
          ),
          validateOnChange: true,
        };
      }
      break;
    case FORM_SUBMITTER_ACTIONS.ITEMS_FILTERED_FROM_ADD:
      newState = {
        ...state,
        errorNotificationMessage: ERRORS.ITEMS_FILTERED_FROM_ADD,
      };
      break;
    case FORM_SUBMITTER_ACTIONS.TOO_MANY_FILES:
      newState = {
        ...state,
        isInvalid: true,
        errorNotificationMessage: ERRORS.TOO_MANY_FILES,
      };
      break;
    case FORM_SUBMITTER_ACTIONS.FILE_SIZE_LIMIT_EXCEEDED:
      newState = {
        ...state,
        isInvalid: true,
        errorNotificationMessage: ERRORS.FILE_SIZE_LIMIT_EXCEEDED,
      };
      break;
    case FORM_SUBMITTER_ACTIONS.INVALID_FILE_NAME:
      newState = {
        ...state,
        isInvalid: true,
        errorNotificationMessage: ERRORS.INVALID_FILE_NAME,
      };
      break;
    case FORM_SUBMITTER_ACTIONS.CLOSE_NOTIFICATION:
      newState = {
        ...state,
        errorNotificationMessage: null,
      };
      break;
    case FORM_SUBMITTER_ACTIONS.SUBMIT_IN_PROGRESS:
      newState = {
        ...state,
        submitInProgress: true,
        isInvalid: false,
      };
      break;
    case FORM_SUBMITTER_ACTIONS.UPLOAD_IN_PROGRESS:
      {
        const { uploader } = action;
        newState = {
          ...state,
          uploader,
          isUploadsManagerVisible: true,
          isUploadManagerExpanded: true,
        };
      }
      break;
    case FORM_SUBMITTER_ACTIONS.UPLOAD_PROGRESS_UPDATE:
      newState = {
        ...state,
        uploader: state.uploader,
      };
      break;
    case FORM_SUBMITTER_ACTIONS.PROCESSING_IN_PROGRESS:
      newState = {
        ...state,
        processingInProgress: true,
      };
      break;
    case FORM_SUBMITTER_ACTIONS.SUBMISSION_FAILED: {
      newState = {
        ...state,
        isInvalid: true,
        errorNotificationMessage: ERRORS.SUBMISSION_FAILED,
        submitInProgress: false,
        submissionFailed: true,
        processingInProgress: false,
      };
      break;
    }
    case FORM_SUBMITTER_ACTIONS.UPDATE_COMPONENTS: {
      const updatedComponents = action.payload;

      newState = {
        ...state,
        components: updatedComponents,
      };
      break;
    }
    default:
  }
  return newState;
};
